import React, { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
  Container,
  Spinner,
  useToaster
} from 'react-angle-dashboard-components'
import {
  useCensusInfo,
  useCloneQuoteToDraft,
  usePastMedicalPlans,
  useQuote,
  useTriggerQuoteRateCalculation,
  useUpdateQuote
} from '../../hooks/quote.hooks'
import { mapQuoteDtoToQuoteView } from '../../services/quote/mappers'
import QuoteView from '../../components/QuoteView/QuoteView'
import { Layout } from '../../layout'
import FooterButtons from '../../components/FooterButtons/FooterButtons'
import { ICensusMemberDTO, IQuoteStatus } from '../../services/quote/dto'
import { Paths } from '../../routes/paths'
import SoldQuoteFormModal from '../../components/SoldQuoteFormModal/SoldQuoteFormModal'
import EditProposalModal from './EditProposalModal/EditProposalModal'

const getNextStatus = (currentStatus: IQuoteStatus): IQuoteStatus | null => {
  switch (currentStatus) {
    case 'draft':
      return 'in_review'
    case 'in_review':
      return 'quoted'
    case 'quoted':
      return 'proposal'
    case 'proposal':
      return 'sold'
    default:
      return null
  }
}

const getContinueButtonText = (currentStatus: IQuoteStatus): string => {
  switch (currentStatus) {
    case 'in_review':
      return 'Move to quoted (temp)'
    case 'quoted':
      return 'Move to proposal'
    case 'proposal':
      return 'Move to signature'
    default:
      return ''
  }
}

const ViewQuote = () => {
  const history = useHistory()
  const { showError } = useToaster()

  const { quoteId } = useParams<any>()

  const { data: quote, isLoading: currentQuoteDataLoading } = useQuote(quoteId)
  const { data: census } = useCensusInfo(quoteId)

  const { data: rateCalcReturn, isError: rateStatusErrored } =
    useTriggerQuoteRateCalculation(quoteId, true)

  const { data: pastMedPlans, isLoading: currentPastMedPlansLoading } =
    usePastMedicalPlans(quoteId)

  const { mutateAsync: updateQuote, isLoading: isUpdateLoading } =
    useUpdateQuote()

  const { mutateAsync: cloneQuote } = useCloneQuoteToDraft()
  const [showEditProposalModal, setShowEditProposalModal] = useState(false)

  const [isMarkAsSoldModalOpen, setIsMarkAsSoldModalOpen] = useState(false)

  if (
    currentQuoteDataLoading ||
    currentPastMedPlansLoading ||
    !quote ||
    !pastMedPlans
  ) {
    return <Spinner animation="border" />
  }

  const canEditQuote =
    quote.status === 'draft' ||
    quote.status === 'quoted' ||
    quote.status === 'proposal'

  const showBeigeBackground =
    quote.status === 'proposal' || quote.status === 'sold'

  const onEditQuote = async () => {
    if (quote.status === 'proposal') {
      setShowEditProposalModal(true)
    } else {
      history.push(Paths.quoteFormQuoteReview(quoteId))
    }
  }

  const cloneAndEditQuote = async () => {
    const res = await cloneQuote(quoteId)
    history.push(Paths.quoteFormQuoteReview(res.newQuote.toString()))
  }

  const quoteViewData = mapQuoteDtoToQuoteView(
    quote,
    quote.prevFundingType === null || pastMedPlans.length > 0
  )

  const hideFooterButtons =
    quote.status === 'in_review' ||
    quote.status === 'archived' ||
    quote.status === 'declined' ||
    quote.status === 'sold' ||
    quote.status === 'error'

  const saveStateChangeToBackend = async (nextStatus: IQuoteStatus) => {
    try {
      if (quoteId) {
        await updateQuote({
          quoteId,
          data: {
            status: nextStatus
          }
        })
      }
    } catch (err) {
      console.error(err)
      showError(
        'There was a problem submitting your quote. Please try again or contact support.'
      )
    }
  }

  const onSubmit = async () => {
    const nextStatus = getNextStatus(quote.status)
    if (nextStatus) {
      if (nextStatus === 'sold') {
        // display modal
        setIsMarkAsSoldModalOpen(true)
      } else {
        saveStateChangeToBackend(nextStatus)
      }
    }
  }

  const onCancel = () => {
    history.push(Paths.quotes)
  }

  const rateStatus = rateStatusErrored ? 'error' : rateCalcReturn?.status

  return (
    <Layout showUserDropdown>
      <div
        style={{
          backgroundColor: showBeigeBackground ? '#F6F2EC' : ''
        }}
      >
        <Container className="p-4">
          <SoldQuoteFormModal
            open={isMarkAsSoldModalOpen}
            onClose={() => setIsMarkAsSoldModalOpen(false)}
            quoteIdToEdit={quoteId}
          />
          <EditProposalModal
            open={showEditProposalModal}
            onClose={() => setShowEditProposalModal(false)}
            onContinue={cloneAndEditQuote}
          />
          <QuoteView
            quote={quoteViewData}
            quoteStatus={quote.status}
            ratesStatus={rateStatus}
            census={census as ICensusMemberDTO[]}
          />
          {!hideFooterButtons && (
            <FooterButtons
              onContinue={onSubmit}
              onBack={onEditQuote}
              onCancel={onCancel}
              continueButtonText={getContinueButtonText(quote.status)}
              isLoading={isUpdateLoading}
              hideBackButton={!canEditQuote}
              backButtonText="Edit"
            />
          )}
        </Container>
      </div>
    </Layout>
  )
}

export default ViewQuote
